import type { KeyboardEvent, RefObject } from 'react';

interface Stage { id: string; label: string }

interface Props {
  stages: Stage[];
  active: number;
  onSelect: (i: number) => void;
  viewportRef: RefObject<HTMLDivElement | null>;
  trackRef: RefObject<HTMLDivElement | null>;
}

const MINOR_TICKS = 'repeating-linear-gradient(90deg, #393D45 0 1px, transparent 1px calc(var(--gap) / 8))';
const MAJOR_TICKS = 'repeating-linear-gradient(90deg, #5A5F69 0 1px, transparent 1px calc(var(--gap) / 2))';
const EDGE_FADE = 'linear-gradient(90deg, transparent 0, #000 6%, #000 90%, transparent 100%)';

export default function ProcessTrack({ stages, active, onSelect, viewportRef, trackRef }: Props) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); onSelect(Math.min(active + 1, stages.length - 1)); }
    if (e.key === 'ArrowLeft') { e.preventDefault(); onSelect(Math.max(active - 1, 0)); }
  };

  return (
    <div className="relative">
      <span className="pointer-events-none absolute -top-6 right-0 flex items-center gap-1 text-[10px] tracking-[0.3em] text-[#777D87] whitespace-nowrap">
        <i className="ri-arrow-left-s-line" />DRAG<i className="ri-arrow-right-s-line" />
      </span>
      <div
        ref={viewportRef}
        onKeyDown={onKey}
        role="group"
        aria-label="Repair stages timeline"
        className="relative h-[132px] overflow-hidden select-none touch-pan-y cursor-grab active:cursor-grabbing [--marker:22%]"
        style={{ maskImage: EDGE_FADE, WebkitMaskImage: EDGE_FADE }}
      >
        <div ref={trackRef} className="absolute top-0 left-0 h-full will-change-transform [--gap:320px]" style={{ width: 'calc(var(--gap) * 4 + 100vw)' }}>
          <div className="absolute top-[62px] right-0 h-px bg-[#393D45]" style={{ left: '-100vw' }} />
          <div className="absolute top-[57px] right-0 h-[5px] opacity-70" style={{ left: '-100vw', backgroundImage: MINOR_TICKS }} />
          <div className="absolute top-[54px] right-0 h-[8px]" style={{ left: '-100vw', backgroundImage: MAJOR_TICKS }} />
          <div data-fill className="absolute top-[62px] left-0 h-px bg-[#D42B38]/80" />

          {stages.map((s, i) => {
            const on = i === active;
            const done = i < active;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelect(i)}
                aria-current={on ? 'step' : undefined}
                aria-label={`Stage ${i + 1}: ${s.label}`}
                className="group absolute top-0 h-full w-[160px] text-left cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-[#EF616B]"
                style={{ left: `calc(var(--gap) * ${i})` }}
              >
                <span className={`absolute top-[32px] left-3 font-heading text-[12px] tracking-[0.25em] transition-colors duration-300 ${on ? 'text-[#D42B38]' : 'text-[#777D87] group-hover:text-[#F5F5F6]'}`}>
                  0{i + 1}
                </span>
                <span className={`absolute left-0 top-[52px] w-px h-[12px] transition-colors duration-300 ${on || done ? 'bg-[#D42B38]' : 'bg-[#777D87]'}`} />
                <span className={`absolute top-[80px] left-3 font-heading uppercase leading-none whitespace-nowrap tracking-[0.04em] transition-colors duration-300 ${on ? 'text-[#F5F5F6] text-[22px] md:text-[24px]' : 'text-[#777D87] text-[13px] md:text-[14px] tracking-[0.15em] group-hover:text-[#F5F5F6]'}`}>
                  {s.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* compact stationary precision tick */}
        <div className="pointer-events-none absolute top-[50px] z-10 h-[24px] w-px bg-[#D42B38]" style={{ left: 'var(--marker)' }}>
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-[5px] bg-[#D42B38]" />
        </div>
      </div>
    </div>
  );
}